import type { JSX } from 'react';
import { Alert, Anchor, Badge, Button, Center, Group, Loader, Paper, Stack, Table, Text } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, templatesApi, type TemplateKind } from '../api';
import { formatBytes, formatDateTime } from '../format';
import { FileDrop } from '../ui/FileDrop';

/**
 * Einstellungen › Vorlagen (AP-91): the office's templates the files are
 * made from. Stored unchanged and in versions; each upload is checked
 * against what the module expects, and only a passing version can be active.
 */
export function TemplatesPanel(): JSX.Element {
  const list = useQuery({ queryKey: ['templates'], queryFn: templatesApi.overview });
  if (list.isPending) return <Center h={120}><Loader size="sm" /></Center>;
  if (list.isError) return <Alert color="red">{list.error instanceof ApiError ? list.error.message : 'Die Vorlagen konnten nicht geladen werden.'}</Alert>;
  if (list.data.length === 0) return <Text size="sm" c="dimmed">Kein aktives Modul arbeitet mit Vorlagen.</Text>;
  return (
    <Stack gap="md">
      <Text size="xs" c="dimmed">Vorlagen des Büros, aus denen PST Dateien erzeugt. Die Vorlage selbst wird nie verändert; PST füllt je Angebot eine Kopie. Jede hochgeladene Datei wird geprüft, nur eine bestandene Version kann aktiv sein.</Text>
      {list.data.map((k) => <KindCard key={k.kind} kind={k} />)}
    </Stack>
  );
}

function KindCard({ kind: k }: { kind: TemplateKind }): JSX.Element {
  const queryClient = useQueryClient();
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['templates'] });
  const upload = useMutation({ mutationFn: (file: File) => templatesApi.upload(k.kind, file, true), onSettled: refresh });
  const activate = useMutation({ mutationFn: (id: string) => templatesApi.activate(id), onSettled: refresh });
  const remove = useMutation({ mutationFn: (id: string) => templatesApi.remove(id), onSettled: refresh });
  const error = upload.error ?? activate.error ?? remove.error;
  const last = upload.data;
  return (
    <Paper withBorder radius="md" p="md">
      <Stack gap="xs">
        <Group justify="space-between"><Text fw={600}>{k.label}</Text>{k.versions.some((v) => v.active) ? <Badge color="green" variant="light">aktiv: Version {k.versions.find((v) => v.active)!.version}</Badge> : <Badge color="orange" variant="light">keine aktive Vorlage</Badge>}</Group>
        <Text size="xs" c="dimmed">{k.description}</Text>
        <FileDrop accept={k.extension} label={`${k.extension}-Datei hierher ziehen oder auswählen – wird als neue Version geprüft`} onFiles={(f) => f[0] && upload.mutate(f[0])} disabled={upload.isPending} />
        {upload.isPending && <Text size="xs" c="dimmed">wird geprüft …</Text>}
        {last && <Alert color={last.check.ok ? 'green' : 'red'} py={6}>{last.check.ok ? `Version ${last.version} bestanden${last.active ? ' und aktiv' : ''}.` : `Version ${last.version} nicht bestanden: ${last.check.problems.join('; ')}`}</Alert>}
        {error && <Alert color="red" py={6}>{error instanceof ApiError ? error.message : 'Das hat nicht geklappt.'}</Alert>}
        {k.versions.length > 0 && (
          <Table verticalSpacing={3}>
            <Table.Thead><Table.Tr><Table.Th w={70}>Version</Table.Th><Table.Th>Datei</Table.Th><Table.Th w={150}>hochgeladen</Table.Th><Table.Th>Prüfung</Table.Th><Table.Th w={200} /></Table.Tr></Table.Thead>
            <Table.Tbody>
              {k.versions.map((v) => (
                <Table.Tr key={v.id}>
                  <Table.Td><Text size="sm">{v.version}{v.active ? ' ·' : ''}</Text></Table.Td>
                  <Table.Td><Anchor size="sm" href={templatesApi.fileUrl(v.id)}>{v.fileName}</Anchor><Text size="xs" c="dimmed">{formatBytes(v.bytes)}</Text></Table.Td>
                  <Table.Td><Text size="xs">{formatDateTime(v.createdAt)}</Text></Table.Td>
                  <Table.Td>{v.check.ok ? <Text size="xs" c="green.7">bestanden · {v.check.notes.join(' · ')}</Text> : <Text size="xs" c="red.7">{v.check.problems.join('; ')}</Text>}</Table.Td>
                  <Table.Td>
                    <Group gap={4} justify="flex-end">
                      {!v.active && v.check.ok && <Button size="compact-xs" variant="light" onClick={() => activate.mutate(v.id)}>aktivieren</Button>}
                      {!v.active && <Button size="compact-xs" variant="subtle" color="red" onClick={() => { if (window.confirm(`Version ${v.version} löschen?`)) remove.mutate(v.id); }}>löschen</Button>}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Paper>
  );
}
